import { Injectable } from "@angular/core";
import { Observable, forkJoin, map, of, take } from "rxjs";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IdMap } from "@gtmhub/util";
import { HttpActions } from "@webapp/core/abstracts/enums/http-actions.enum";
import { UsersFacade } from "@webapp/users/services/users-facade.service";
import {
  IBuildSessionTreeContext,
  ISessionListItem,
  ISessionSelectorContext,
  ISessionData as ISessionSelectorData,
  ISessionTreeNode,
} from "../models/session-tree.model";
import { Session } from "../models/sessions.model";
import { getSessionAndOneLevelChildren } from "../utils/utils";
import { SessionSelectorUtilsService } from "./session-selector-utils.service";

@Injectable({
  providedIn: "root",
})
export class SessionSelectorFacade {
  private sessionIdMap: IdMap<Session>;

  constructor(private usersFacade: UsersFacade, private sessionSelectorUtilsService: SessionSelectorUtilsService) {}

  // This is logic only for constructing the session tree for the okr create form
  public getSessionData$(context: ISessionSelectorContext): Observable<ISessionSelectorData> {
    const sessionsState = reduxStoreContainer.reduxStore.getState<ISessionsStoreState>().sessions;
    this.sessionIdMap = sessionsState.map;

    const allSessions$ = of(sessionsState.items);
    return forkJoin({ allSessions: allSessions$ }).pipe(
      take(1),
      map((result) => {
        const sessionsSetWithPermissions = this.sessionSelectorUtilsService.fillSessionWithPermissions(result.allSessions);
        let sessionsToCreateNodesFrom = sessionsSetWithPermissions;

        if (context.parentItem && context.currentSession) {
          sessionsToCreateNodesFrom = getSessionAndOneLevelChildren(sessionsSetWithPermissions, context.parentItem.sessionId);
        }

        return { tree: this.buildTree(sessionsSetWithPermissions, sessionsToCreateNodesFrom, context), list: this.buildList(sessionsToCreateNodesFrom) };
      })
    );
  }

  // The sessions are loaded from redux and are loaded before initialisation of session-selector as they are fetched in goals/form.ts
  public isLoading$(): Observable<boolean> {
    return this.usersFacade.getState().isLoading$(HttpActions.get);
  }

  private buildList(selectableSessions: Session[]): ISessionListItem[] {
    return selectableSessions.map((session) => this.createSessionListItem(session)).filter((session) => !session.disabled);
  }

  private buildTree(allSessions: Session[], selectableSessions: Session[], context: ISessionSelectorContext): ISessionTreeNode[] {
    let subTreeBuild = false;
    let filterWithHostId = !!context.goal?.parentId;
    const subTreeRootLevelSessionId = context.currentSession?.parentId;

    // Whenever we have our goal aligned we want to show only our current session and its children
    if (context.parentItem && context.currentSession) {
      const currentGoalAndParentItemAreInSharedSession = context.currentSession?.id === context.parentItem.sessionId;
      filterWithHostId = currentGoalAndParentItemAreInSharedSession;
      subTreeBuild = allSessions.find((session) => session.id === context.parentItem.sessionId)?.parentId && !currentGoalAndParentItemAreInSharedSession;
    }

    const rootLevelSessions = !subTreeBuild
      ? selectableSessions
          .filter((s) => !s.parentId || !this.sessionIdMap[s.parentId] || (filterWithHostId && s.id === context.currentSession?.id))
          .sort(this.sessionSelectorUtilsService.byEndDescAndStartAsc)
      : selectableSessions.filter((s) => s.id === subTreeRootLevelSessionId);

    const parentNodes = rootLevelSessions.map((session) =>
      this.createSessionTreeItem(session.id, { sessions: selectableSessions, hostSessionId: context.currentSession?.id })
    );

    const hostNode = parentNodes.find((n) => n.isHostSession || n.hasChildHostSession);
    return hostNode ? [hostNode, ...parentNodes.filter((n) => n.key !== hostNode.key)] : parentNodes;
  }

  private createSessionTreeItem(sessionId: string, context: IBuildSessionTreeContext): ISessionTreeNode {
    const session = context.sessions.find((s) => s.id === sessionId);
    const sessionItem: ISessionTreeNode = {
      key: sessionId,
      title: session.title,
      color: session.color,
      isHostSession: !!(context.hostSessionId && sessionId === context.hostSessionId),
      disabled: !session.userHasPermissionToCreate,
    } as ISessionTreeNode;
    this.setChildrenPropertiesToSessionItem(sessionItem, { hostSessionId: context.hostSessionId, sessions: context.sessions });

    return sessionItem;
  }

  private createSessionListItem(session: Session): ISessionListItem {
    const sessionItem: ISessionListItem = {
      key: session.id,
      title: session.title,
      color: session.color,
      disabled: !session.userHasPermissionToCreate,
    };

    return sessionItem;
  }

  private setChildrenPropertiesToSessionItem(sessionItem: ISessionTreeNode, context: IBuildSessionTreeContext): void {
    sessionItem.children = [];
    const childSessionsIds = context.sessions
      .filter((s) => s.parentId === sessionItem.key)
      .sort(this.sessionSelectorUtilsService.byEndDescAndStartAsc)
      .map((cs) => cs.id);

    if (childSessionsIds.length === 0) {
      sessionItem.hasChildHostSession = false;
    } else {
      childSessionsIds.forEach((childSessionId) => {
        sessionItem.children.push(this.createSessionTreeItem(childSessionId, { sessions: context.sessions, hostSessionId: context.hostSessionId }));
      });

      sessionItem.hasChildHostSession = !!(context.hostSessionId && this.sessionSelectorUtilsService.isAnyChild(sessionItem, (c) => c.key === context.hostSessionId));
    }
  }
}
